import { create } from "zustand";
import type { Brand } from "../../types";
import { api } from "@/api/client";

interface BrandsStore {
  brands: Brand[];
  loading: boolean;
  error: string | null;
  fetch: () => Promise<void>;
  add: (name: string, description?: string) => Promise<void>;
  update: (id: number, fields: { name?: string; description?: string }) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

// The in-flight `fetch()` promise, if any. Several views mount at once and each
// calls `fetch()` from its own effect, which used to fire duplicate
// `/api/brands` requests on every page load. Kept outside the zustand state:
// it's plumbing, not something a component renders on.
let inFlight: Promise<void> | null = null;

// Bumped by every mutation. A read that started before the bump is stale, so it
// is discarded rather than allowed to overwrite post-mutation state — without
// this, sharing an in-flight promise with a mutation's refetch could resurrect
// a brand the user just deleted.
let generation = 0;

/** Starts a fresh list read, replacing any shared in-flight promise. */
function loadBrands(set: (partial: Partial<BrandsStore>) => void): Promise<void> {
  const startedAt = generation;
  set({ loading: true });
  inFlight = (async () => {
    try {
      const brands = await api.brands.list();
      if (startedAt !== generation) return; // a mutation landed mid-flight
      set({ brands, loading: false, error: null });
    } catch (err: any) {
      if (startedAt !== generation) return;
      set({ loading: false, error: err?.message || "Failed to load brands" });
    } finally {
      // Only clear the slot if a newer read hasn't already claimed it.
      if (startedAt === generation) inFlight = null;
    }
  })();
  return inFlight;
}

export const useBrandsStore = create<BrandsStore>((set) => ({
  brands: [],
  loading: false,
  error: null,

  fetch: () => inFlight ?? loadBrands(set),

  add: async (name, description) => {
    await api.brands.create(name, description);
    generation++;
    await loadBrands(set);
  },

  update: async (id, fields) => {
    await api.brands.update(id, fields);
    generation++;
    await loadBrands(set);
  },

  remove: async (id) => {
    await api.brands.delete(id);
    generation++;
    await loadBrands(set);
  },
}));
